import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ClientSearch } from "@/components/common/ClientSearch";
import { toast } from "sonner";
import { Users, Gift, CheckCircle2, XCircle, Clock } from "lucide-react";

// Pannello Admin: parametri programma "Porta un'Amica" + coda referral da confermare.
export function ReferralSettingsPanel({ canWrite }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const [pickers, setPickers] = useState({}); // referralId -> selected referrer client

  const loadSettings = () => api.get("/referral/settings").then((r) => setS(r.data)).catch(() => {});
  const loadPending = () => api.get("/referrals?status=pending").then((r) => setPending(r.data || [])).catch(() => {});
  useEffect(() => { loadSettings(); loadPending(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!s.enabled,
        new_client_discount_pct: Number(s.new_client_discount_pct) || 0,
        reward_pct: Number(s.reward_pct) || 0,
        threshold: Math.max(1, parseInt(s.threshold) || 1),
        reward_expiry_days: Math.max(1, parseInt(s.reward_expiry_days) || 60),
        reward_max_cap_eur: s.reward_max_cap_eur === "" || s.reward_max_cap_eur == null ? null : Number(s.reward_max_cap_eur),
        reward_stackable: !!s.reward_stackable,
      };
      const { data } = await api.put("/referral/settings", payload);
      setS(data);
      toast.success("Impostazioni referral salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const confirm = async (rid) => {
    const referrer = pickers[rid];
    if (!referrer) { toast.error("Seleziona la cliente che ha presentato l'amica"); return; }
    try {
      await api.post(`/referrals/${rid}/confirm`, { referrer_id: referrer.id });
      toast.success("Referral confermato");
      setPickers((p) => { const n = { ...p }; delete n[rid]; return n; });
      loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore conferma"); }
  };

  const invalidate = async (rid) => {
    try {
      await api.post(`/referrals/${rid}/invalidate`, { reason: "Non valido" });
      toast.success("Referral annullato");
      loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!s) return <Card className="p-6 text-sm text-muted-foreground">Caricamento…</Card>;

  const Num = ({ label, field, suffix, placeholder }) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <Input type="number" value={s[field] ?? ""} disabled={!canWrite}
          onChange={(e) => setS({ ...s, [field]: e.target.value })}
          placeholder={placeholder} data-testid={`referral-set-${field}`} className="h-9" />
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="referral-settings-panel">
      <Card className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--hi-gold)/0.15)] text-[hsl(var(--hi-gold))]"><Gift className="h-5 w-5" /></div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold">Programma "Porta un'Amica"</h3>
            <p className="text-sm text-muted-foreground">Regole di sconto e premio per il passaparola.</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="ref-enabled" className="text-xs">Attivo</Label>
            <Switch id="ref-enabled" checked={!!s.enabled} disabled={!canWrite}
              onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="referral-set-enabled" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Num label="Sconto benvenuto nuova cliente" field="new_client_discount_pct" suffix="% (solo servizi)" />
          <Num label="Premio al presentatore" field="reward_pct" suffix="%" />
          <Num label="Amiche necessarie per il premio" field="threshold" suffix="referral" />
          <Num label="Scadenza premio" field="reward_expiry_days" suffix="giorni" />
          <Num label="Tetto massimo premio" field="reward_max_cap_eur" suffix="€ (vuoto = nessun tetto)" placeholder="nessun tetto" />
          <div className="space-y-1.5">
            <Label className="text-xs">Cumulabile con altri premi</Label>
            <div className="flex items-center gap-2 h-9">
              <Switch checked={!!s.reward_stackable} disabled={!canWrite}
                onCheckedChange={(v) => setS({ ...s, reward_stackable: v })} data-testid="referral-set-stackable" />
              <span className="text-xs text-muted-foreground">{s.reward_stackable ? "Sì" : "No (consigliato)"}</span>
            </div>
          </div>
        </div>
        {canWrite && <Button className="mt-4" onClick={save} disabled={saving} data-testid="referral-settings-save">Salva impostazioni</Button>}
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-[hsl(var(--hi-gold))]" />
          <h3 className="font-heading text-lg font-semibold">Referral da confermare</h3>
          <Badge variant="secondary" className="ml-1" data-testid="referral-pending-count">{pending.length}</Badge>
        </div>
        {pending.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Nessun referral in attesa di conferma.</div>
        ) : (
          <div className="space-y-3" data-testid="referral-pending-list">
            {pending.map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`referral-pending-${r.id}`}>
                <div className="flex items-center gap-2 text-sm mb-2">
                  <Clock className="h-4 w-4 text-[hsl(var(--hi-status-warning,38_92%_50%))]" />
                  <span>Nuova cliente: <b>{r.referred_name || "—"}</b></span>
                  {r.referrer_name_raw && <span className="text-muted-foreground">· presentata da "{r.referrer_name_raw}"</span>}
                </div>
                {canWrite && (
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <div className="flex-1"><ClientSearch value={pickers[r.id] || null} onSelect={(c) => setPickers((p) => ({ ...p, [r.id]: c }))} /></div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => confirm(r.id)} disabled={!pickers[r.id]} data-testid={`referral-confirm-${r.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Conferma</Button>
                      <Button size="sm" variant="outline" onClick={() => invalidate(r.id)} data-testid={`referral-invalidate-${r.id}`}><XCircle className="h-4 w-4 mr-1" /> Annulla</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
